/**
 * Grim Walker — Elemental system (frontend mirror of backend/combat/elements.py).
 *
 * Never hardcode element names anywhere else in the UI. Import ELEMENTS from
 * here and use ELEMENT_META to render colours, icons, and localized labels.
 *
 * The interaction matrix is fetched at runtime from /api/combat/elements so
 * balance changes on the backend take effect without a frontend redeploy.
 */

export const ELEMENTS = [
  "Fire", "Ice", "Lightning", "Poison", "Darkness",
  "Holy", "Arcane", "Earth", "Water", "Wind",
];

/**
 * Legacy → canonical mapping used when displaying items or enemies that were
 * stored before the Shadow → Darkness rename. Always call normalizeElement()
 * before using an element string for lookup.
 */
export const LEGACY_ELEMENT_ALIASES = {
  shadow: "Darkness",
  dark: "Darkness",
  necrotic: "Darkness",
  light: "Holy",
  radiant: "Holy",
  nature: "Earth",
  storm: "Lightning",
  shock: "Lightning",
  frost: "Ice",
  flame: "Fire",
  venom: "Poison",
  aqua: "Water",
  air: "Wind",
};

export function normalizeElement(v) {
  if (!v) return null;
  if (ELEMENTS.includes(v)) return v;
  const alias = LEGACY_ELEMENT_ALIASES[String(v).toLowerCase()];
  if (alias) return alias;
  const cap = String(v).charAt(0).toUpperCase() + String(v).slice(1).toLowerCase();
  return ELEMENTS.includes(cap) ? cap : null;
}

/**
 * Visual metadata for the 10 canonical elements. Colours are chosen to fit
 * the dark-fantasy palette already used elsewhere in Grim Walker.
 */
export const ELEMENT_META = {
  Fire:      { color: "#E55A00", glow: "rgba(229, 90, 0, 0.5)",  icon: "flame",       label: "Fuoco",      short: "🔥" },
  Ice:       { color: "#5AB5E8", glow: "rgba(90, 181, 232, 0.5)", icon: "snowflake",   label: "Ghiaccio",   short: "❄" },
  Lightning: { color: "#E5C34A", glow: "rgba(229, 195, 74, 0.5)", icon: "zap",         label: "Fulmine",    short: "⚡" },
  Poison:    { color: "#6E8B3D", glow: "rgba(110, 139, 61, 0.5)", icon: "skull",       label: "Veleno",     short: "☠" },
  Darkness:  { color: "#5D2F5F", glow: "rgba(93, 47, 95, 0.6)",   icon: "moon",        label: "Oscurità",   short: "🌑" },
  Holy:      { color: "#E5D6A4", glow: "rgba(229, 214, 164, 0.7)", icon: "sun",        label: "Luce Sacra", short: "✨" },
  Arcane:    { color: "#A97AB0", glow: "rgba(169, 122, 176, 0.6)", icon: "sparkles",   label: "Arcano",     short: "✦" },
  Earth:     { color: "#8B6A3A", glow: "rgba(139, 106, 58, 0.5)",  icon: "mountain",   label: "Terra",      short: "⛰" },
  Water:     { color: "#3F7D8F", glow: "rgba(63, 125, 143, 0.5)",  icon: "droplet",    label: "Acqua",      short: "💧" },
  Wind:      { color: "#B0C4B1", glow: "rgba(176, 196, 177, 0.5)", icon: "wind",       label: "Vento",      short: "🌪" },
};

/**
 * Runtime cache for the interaction matrix. Populated by fetchElementMatrix().
 * Never mutate directly.
 */
let _matrixCache = null;

export async function fetchElementMatrix(apiClient) {
  if (_matrixCache) return _matrixCache;
  const data = await apiClient.get("/combat/elements");
  _matrixCache = data.matrix || {};
  return _matrixCache;
}

export function getMatrixMultiplier(attacker, defender) {
  if (!_matrixCache) return 1.0;
  const a = normalizeElement(attacker);
  const d = normalizeElement(defender);
  if (!a || !d) return 1.0;
  return _matrixCache[a]?.[d] ?? 1.0;
}
